<template>
    <!-- Onglet Produits (remplace « Rentabilité ABC ») : chargé à sa première ouverture -->
    <div v-if="chargement && !articles.length" class="pp-vide"><i class="ri-loader-4-line pp-spin"></i> Analyse des produits…</div>
    <div v-else-if="erreur" class="pp-vide pp-txt-d">Impossible de charger les produits.</div>
    <div v-else-if="!articles.length" class="pp-vide">Aucune vente sur les 30 derniers jours.</div>
    <div v-else>
        <div class="pp-minis">
            <div class="pp-mk"><span>Classe A</span><b class="pp-txt-s">{{ resume.A }}</b><em>font 70 % du CA</em></div>
            <div class="pp-mk"><span>Classe B</span><b>{{ resume.B }}</b><em>les 20 % suivants</em></div>
            <div class="pp-mk"><span>Classe C</span><b class="pp-txt-m">{{ resume.C }}</b><em>les 10 % restants</em></div>
            <div class="pp-mk"><span>Sans prix d'achat</span><b class="pp-txt-w">{{ nbInconnus }}</b><em>marge inconnue</em></div>
        </div>

        <div class="pp-g2">
            <div class="pp-bloc">
                <div class="pp-bloc__tete">
                    <div class="pp-filtres">
                        <div class="pp-recherche"><i class="ri-search-line"></i><input v-model="recherche" class="pp-inp" placeholder="Rechercher un article"></div>
                        <button v-for="f in filtres" :key="f.id" type="button" class="pp-chip" :class="{ 'pp-chip--on': filtre === f.id }" @click="filtrer(f.id)">{{ f.libelle }}</button>
                    </div>
                    <button type="button" class="ds-btn-ghost !py-1 !px-2 !text-[0.75rem]" @click="exporterCsv"><i class="ri-download-2-line"></i>CSV</button>
                </div>
                <div class="pp-wrap">
                    <table class="pp-table">
                        <thead><tr>
                            <th v-for="c in colonnes" :key="c.id" class="th" :class="{ 'pp-r': c.droite, 'pp-tri': c.tri }" @click="c.tri && trier(c.id)">
                                {{ c.libelle }}<i v-if="tri === c.id" :class="sens < 0 ? 'ri-arrow-down-line' : 'ri-arrow-up-line'"></i>
                            </th>
                        </tr></thead>
                        <tbody>
                            <tr v-for="a in visibles" :key="a.pid">
                                <td class="td"><b>{{ a.libelle }}</b><small>{{ a.reference || '-' }} · classe <b>{{ a.classe_abc }}</b></small></td>
                                <td class="td pp-r">{{ nombre(a.volume) }}</td>
                                <td class="td pp-r"><b>{{ montant(a.ca_genere) }}</b></td>
                                <td class="td pp-r">
                                    <span v-if="a.taux_marge === null" class="pp-txt-w">n.d.</span>
                                    <template v-else>{{ pourcent(a.taux_marge, 1) }}<small>{{ montant(a.marge_unitaire) }} / unité</small></template>
                                </td>
                                <td class="td">
                                    <div class="pp-barre"><div class="pp-jauge"><span :style="{ width: (a.part / partMax * 100) + '%' }"></span></div><span class="pp-txt-m">{{ pourcent(a.part, 1) }}</span></div>
                                </td>
                                <td class="td"><span class="pp-badge" :class="ROLES[a.classe_bcg][1]">{{ ROLES[a.classe_bcg][0] }}</span></td>
                            </tr>
                            <tr v-if="!filtres_.length"><td class="td pp-vide" colspan="6">Aucun article ne correspond.</td></tr>
                        </tbody>
                    </table>
                </div>
                <div v-if="filtres_.length > visibles.length" class="pp-plus">
                    <button type="button" class="ds-btn-ghost !py-1 !px-3 !text-[0.75rem]" @click="nbAffiches += 100">
                        Afficher 100 de plus ({{ visibles.length }} sur {{ filtres_.length }})
                    </button>
                </div>
            </div>

            <div class="pp-bloc">
                <div class="pp-bloc__tete">
                    <div class="pp-bloc__titre"><i class="ri-bubble-chart-line"></i>Volume et marge <small>30 derniers jours</small></div>
                </div>
                <div class="pp-bloc__corps">
                    <apexchart v-if="visible" type="scatter" height="300" :options="optionsBcg" :series="seriesBcg"></apexchart>
                    <p class="pp-legende"><i class="ri-information-line"></i><span>
                        <b>Champions</b> : beaucoup vendus et rentables, à ne jamais manquer.
                        <b>Vaches à lait</b> : volume fort, marge faible, revoir le prix.
                        <b>Niches</b> : rentables mais peu vendus, à mettre en avant.
                        <b>Poids morts</b> : à questionner. Comparaison avec la médiane des articles qui ont un prix d'achat.
                    </span></p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import { appelPP, montant, pourcent } from "./appelpp";
import { avecCachePP } from "@/service/cachePrevisionPerformance";

const ROLES = {
    Champions: ["Champion", "pp-b-s", "#26BF94"],
    VachesLait: ["Vache à lait", "pp-b-p", "#845ADF"],
    Niches: ["Niche", "pp-b-i", "#49B6F5"],
    PoidsMorts: ["Poids mort", "pp-b-m", "#94a3b8"],
    Inconnu: ["Prix d'achat manquant", "pp-b-w", "#F5B849"]
};

export default {
    name: "TabProduits",
    components: { apexchart: VueApexCharts },
    emits: ["cache"],
    props: {
        magasin: { type: String, default: "" },
        actif: { type: Boolean, default: false },     // onglet déjà ouvert une fois
        visible: { type: Boolean, default: false },   // onglet affiché (rendu du graphique)
        forcer: { type: Number, default: 0 }          // incrémenté par Actualiser
    },
    data() {
        return {
            ROLES,
            chargement: false, erreur: false, aRecharger: true,
            articles: [], resume: { total: 0, A: 0, B: 0, C: 0, ca_total: 0, couverture_pa: 0 },
            filtres: [
                { id: "tous", libelle: "Tous" }, { id: "A", libelle: "Classe A" }, { id: "B", libelle: "B" },
                { id: "C", libelle: "C" }, { id: "inconnu", libelle: "Sans prix d'achat" }
            ],
            colonnes: [
                { id: "libelle", libelle: "Article", tri: true }, { id: "volume", libelle: "Qté", tri: true, droite: true },
                { id: "ca_genere", libelle: "CA", tri: true, droite: true }, { id: "taux_marge", libelle: "Marge", tri: true, droite: true },
                { id: "part", libelle: "Part du CA" }, { id: "classe_bcg", libelle: "Rôle" }
            ],
            filtre: "tous", recherche: "", tri: "ca_genere", sens: -1, nbAffiches: 100
        };
    },
    computed: {
        nbInconnus() { return this.articles.filter((a) => a.taux_marge === null).length; },
        partMax() { return Math.max(...this.articles.map((a) => a.part), 0.0001); },
        filtres_() {
            const q = this.recherche.trim().toLowerCase();
            const l = this.articles.filter((a) =>
                (this.filtre === "tous" || a.classe_abc === this.filtre || (this.filtre === "inconnu" && a.taux_marge === null)) &&
                (!q || a.libelle.toLowerCase().includes(q) || String(a.reference || "").toLowerCase().includes(q)));
            const t = this.tri, s = this.sens;
            return l.sort((x, y) => {
                const a = x[t], b = y[t];
                if (typeof a === "string" || typeof b === "string") return String(a || "").localeCompare(String(b || "")) * s;
                return ((a === null ? -Infinity : a) - (b === null ? -Infinity : b)) * s;
            });
        },
        visibles() { return this.filtres_.slice(0, this.nbAffiches); },
        seriesBcg() {
            return ["Champions", "VachesLait", "Niches", "PoidsMorts"].map((r) => ({
                name: ROLES[r][0],
                data: this.articles.filter((a) => a.classe_bcg === r)
                    .map((a) => ({ x: Math.round(a.volume * 100) / 100, y: Math.round(a.taux_marge * 1000) / 10, nom: a.libelle }))
            }));
        },
        optionsBcg() {
            return {
                chart: { type: "scatter", toolbar: { show: false }, zoom: { enabled: true, type: "xy" }, fontFamily: "inherit" },
                colors: ["Champions", "VachesLait", "Niches", "PoidsMorts"].map((r) => ROLES[r][2]),
                xaxis: { title: { text: "Quantité vendue" }, tickAmount: 4, labels: { formatter: (v) => (v >= 1000 ? (v / 1000).toFixed(1).replace(".", ",") + " k" : Math.round(v)) } },
                yaxis: { title: { text: "Taux de marge" }, labels: { formatter: (v) => Math.round(v) + " %" } },
                markers: { size: 6 },
                legend: { position: "top" },
                grid: { borderColor: "#eef1f6" },
                tooltip: {
                    custom: ({ seriesIndex, dataPointIndex, w }) => {
                        const p = w.config.series[seriesIndex].data[dataPointIndex];
                        return `<div style="padding:6px 10px;font-size:12px"><b>${p.nom}</b><br>${p.x} vendus · marge ${p.y} %</div>`;
                    }
                }
            };
        }
    },
    watch: {
        actif(v) { if (v && this.aRecharger) this.charger(false); },
        magasin() { this.aRecharger = true; if (this.actif) this.charger(false); },
        forcer() { this.aRecharger = true; if (this.actif) this.charger(true); }
    },
    mounted() { if (this.actif) this.charger(false); },
    methods: {
        montant, pourcent,
        nombre(v) { return Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 }); },
        filtrer(id) { this.filtre = id; this.nbAffiches = 100; },
        trier(id) { this.sens = this.tri === id ? -this.sens : (id === "libelle" ? 1 : -1); this.tri = id; },
        async charger(force) {
            this.aRecharger = false;
            this.chargement = true;
            this.erreur = false;
            try {
                const params = { magpid: this.magasin || "" };
                const r = await avecCachePP(this.$store.state.appsociete, "rentabilite-abc", params,
                    () => appelPP(this.$store, "v2/rentabilite-abc", { ...params, limit: 1000 }), { force });
                if (!r.data) { this.erreur = true; return; }
                const total = r.data.resume.ca_total || 1;
                this.resume = r.data.resume;
                this.articles = (r.data.datas || []).map((a) => ({ ...a, part: a.ca_genere / total }));
                this.nbAffiches = 100;
                this.$emit("cache", r.cache ? r.t : null);
            } catch (e) {
                console.error(e);
                this.erreur = true;
            } finally {
                this.chargement = false;
            }
        },
        exporterCsv() {
            const lignes = [["Reference", "Article", "Quantite", "CA", "Taux_marge", "Marge_unitaire", "Part_CA", "Classe_ABC", "Role"],
                ...this.filtres_.map((a) => [a.reference, a.libelle, a.volume, Math.round(a.ca_genere),
                    a.taux_marge === null ? "" : (a.taux_marge * 100).toFixed(2), a.marge_unitaire === null ? "" : Math.round(a.marge_unitaire),
                    (a.part * 100).toFixed(2), a.classe_abc, ROLES[a.classe_bcg][0]])];
            const csv = lignes.map((l) => '"' + l.map((v) => String(v ?? "").replace(/"/g, '""')).join('";"') + '"').join("\n");
            const lien = document.createElement("a");
            lien.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
            lien.download = "produits_rentabilite.csv";
            lien.click();
        }
    }
};
</script>

<style scoped>
.pp-vide { text-align: center; color: #94a3b8; padding: 2.5rem 1rem; }
.pp-spin { display: inline-block; animation: pp-tourne .8s linear infinite; }
@keyframes pp-tourne { to { transform: rotate(360deg); } }
.pp-minis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: .6rem; margin-bottom: 1rem; }
.pp-mk { padding: .7rem .8rem; border-radius: .5rem; background: #f8fafc; border: 1px solid #e2e8f0; }
.pp-mk span { font-size: .7rem; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: .03em; display: block; }
.pp-mk b { display: block; font-size: 1.15rem; font-variant-numeric: tabular-nums; color: #0f172a; }
.pp-mk em { font-style: normal; font-size: .7rem; color: #64748b; }
.pp-g2 { display: grid; grid-template-columns: 3fr 2fr; gap: 1rem; align-items: start; }
.pp-bloc { border: 1px solid #e2e8f0; border-radius: .625rem; background: #fff; min-width: 0; }
.pp-bloc__tete { display: flex; flex-wrap: wrap; gap: .5rem; align-items: center; justify-content: space-between; padding: .8rem 1rem; border-bottom: 1px solid #e2e8f0; }
.pp-bloc__titre { font-weight: 700; color: #0f172a; font-size: .88rem; display: flex; align-items: center; gap: .4rem; }
.pp-bloc__titre i { color: rgb(var(--primary)); }
.pp-bloc__titre small { font-weight: 500; color: #94a3b8; font-size: .72rem; }
.pp-bloc__corps { padding: 1rem; }
.pp-filtres { display: flex; flex-wrap: wrap; gap: .4rem; align-items: center; }
.pp-recherche { position: relative; }
.pp-recherche i { position: absolute; left: .6rem; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.pp-inp { height: 34px; border: 1px solid rgb(var(--input-border, 203 213 225)); border-radius: .5rem; padding: 0 .75rem 0 2rem; font-size: .8rem; outline: none; min-width: 13rem; background: #fff; }
.pp-inp:focus { border-color: rgb(var(--primary)); box-shadow: 0 0 0 3px rgb(var(--primary) / .10); }
.pp-chip { border: 1px solid #e2e8f0; background: #fff; border-radius: 99px; padding: .28rem .7rem; font-size: .74rem; font-weight: 600; color: #64748b; cursor: pointer; }
.pp-chip--on { border-color: rgb(var(--primary) / .4); background: rgb(var(--primary) / .08); color: rgb(var(--primary)); }
.pp-wrap { overflow-x: auto; max-height: 560px; overflow-y: auto; }
.pp-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 620px; }
.th { position: sticky; top: 0; z-index: 2; padding: .6rem .8rem; font-weight: 600; font-size: .72rem; color: #1e293b; background: #f1f5f9; border-bottom: 1px solid #e2e8f0; white-space: nowrap; letter-spacing: .02em; text-align: left; }
.pp-tri { cursor: pointer; user-select: none; }
.pp-tri:hover { color: rgb(var(--primary)); }
.td { padding: .55rem .8rem; border-bottom: 1px solid #e2e8f0; white-space: nowrap; font-variant-numeric: tabular-nums; color: #1e293b; }
.td small { display: block; color: #94a3b8; font-size: .7rem; }
.pp-table tr:hover .td { background: #f8fafc; }
.pp-r { text-align: right !important; }
.pp-barre { display: flex; align-items: center; gap: .5rem; font-size: .7rem; }
.pp-jauge { flex: 1; min-width: 60px; height: 6px; border-radius: 9px; background: #f1f5f9; overflow: hidden; }
.pp-jauge span { display: block; height: 100%; background: rgb(var(--primary) / .7); border-radius: 9px; }
.pp-plus { padding: .75rem; text-align: center; border-top: 1px solid #e2e8f0; }
.pp-legende { font-size: .74rem; color: #64748b; display: flex; gap: .4rem; align-items: flex-start; margin: .75rem 0 0; }
.pp-badge { display: inline-flex; align-items: center; font-size: .68rem; font-weight: 700; padding: .15rem .5rem; border-radius: 99px; white-space: nowrap; }
.pp-b-s { background: rgb(var(--success) / .12); color: rgb(var(--success)); }
.pp-b-w { background: rgb(var(--warning) / .18); color: #9a6c12; }
.pp-b-p { background: rgb(var(--primary) / .1); color: rgb(var(--primary)); }
.pp-b-i { background: rgb(var(--info) / .12); color: #2a8dc6; }
.pp-b-m { background: #f1f5f9; color: #64748b; }
.pp-txt-s { color: rgb(var(--success)) !important; }
.pp-txt-d { color: rgb(var(--danger)) !important; }
.pp-txt-w { color: #b7811c !important; }
.pp-txt-m { color: #94a3b8 !important; }
@media (max-width: 1100px) { .pp-g2 { grid-template-columns: 1fr; } }
@media (max-width: 640px) {
    .pp-minis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .pp-recherche, .pp-inp { width: 100%; min-width: 0; }
}
</style>
